import {
  Container,
  Dimmer,
  Grid,
  Loader,
  Divider,
  Form,
  Header,
  Button,
} from 'semantic-ui-react';
import { connect } from 'react-redux';
import { required, createValidity, assertString } from '@mhub/web-validations';
import clone from 'clone';
import isEqual from 'lodash/isEqual';
import React, { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Auth from '@aws-amplify/auth';
import i18n from 'i18next';
import Timestamp from '../../components/Timestamp';
import { refreshUserCache } from '../../utils/refreshUserCache';
import { languageOptions } from '../../utils/languageOptions';
import { changeDisplayLanguage } from '../../utils/displayLanguage';
import { flashSuccessMessage, flashErrorMessage } from '../../actions/flash';
import { fetchM } from '../../actions/fetchM';
import config from '../../../config';
import INTL_CALL_CODES from '../../constants/intlCallCodes';
import LANGUAGE_CODES from '../../constants/languages';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import NavBar from '../../components/NavBar';
import EditableBox from '../../components/EditableBox';
import Phone from '../../components/Phone';
import AppForm from '../../components/AppForm';
import PhoneInput from '../../components/AppForm/PhoneInput';
import valueToText from '../../utils/valueToText';
import { saveLanguagePreference } from '../../utils/languagePreference';

let pendingProfileWrite = null;
let profileWriteUnconfirmed = false;
let pendingProfileIsAudit = false;
let pendingPasswordChange = null;
const profileStatusListeners = new Set();
const notifyProfileStatus = (fields, userID) =>
  profileStatusListeners.forEach(listener => listener({ fields, userID }));

function profileHasChanges(data, oriData) {
  // Language metadata is not an independently edited profile field.
  const fields = Object.keys({ ...data, ...oriData })
    .filter(key => key !== 'currentLanguageCode' && key !== 'currentLanguageSetAt');
  return fields.some(key => !isEqual(data[key], (oriData || {})[key]));
}

export class Overview extends Component {
  static propTypes = {
    onCheck: PropTypes.func,
    t: PropTypes.func.isRequired,
    i18n: PropTypes.shape({ changeLanguage: PropTypes.func }),
    auth: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      isADFS: PropTypes.bool,
      company: PropTypes.shape({ activeLanguageCodes: PropTypes.arrayOf(PropTypes.string) }),
    }),
  }

  static contextTypes = {
    store: PropTypes.object,
  }

  static mapStateToProps = state => ({
    auth: state.auth,
  })

  constructor(props) {
    super(props);
    this.state = {
      token: null,
      loading: true,
      edit: false,
      editPassword: false,
      data: null,
      oriData: null,
      errMessages: [],
      hasChanges: false,
      passwords: {
        current: '',
        new: '',
        confirm: '',
      },
    };
    this.validation = {
      name: createRef(),
      preferredName: createRef(),
      mobilePrefix: createRef(),
      mobile: createRef(),
    };
    this.passwordValidation = {
      current: createRef(),
      new: createRef(),
      confirm: createRef(),
    };
  }
  componentDidMount() {
    this.profileMounted = true;
    this.updatePendingStatus = ({ fields, userID } = {}) => {
      if (fields && userID === (this.props.auth || {}).id) this.acceptProfileFields(fields);
      const profileSaveWarning = profileWriteUnconfirmed && !pendingProfileIsAudit
        ? this.props.t('Profile save is still unconfirmed. Your edits are preserved. Wait for the connection to recover before retrying.', { ns: TRANSLATION_FILES.translation }) : null;
      this.setState({ profileSaveWarning });
    };
    profileStatusListeners.add(this.updatePendingStatus);
    this.updatePendingStatus();
    const { store } = this.context;

    this.fetchProfile()
      .catch((err) => {
        Bugsnag.notifyException(err);
        store.dispatch(flashErrorMessage(err));
      });
    i18n.on('languageChanged', this.handleI18nChange);
    i18n.on('explicitLanguageSelected', this.handleExplicitLanguageChange);
  }
  componentWillUnmount() {
    this.profileMounted = false;
    profileStatusListeners.delete(this.updatePendingStatus);
    i18n.off('languageChanged', this.handleI18nChange);
    i18n.off('explicitLanguageSelected', this.handleExplicitLanguageChange);
  }
  setComponentState(state = {}, callback) {
    return new Promise((res) => {
      this.setState(state, () => {
        if (callback) {
          callback();
        }
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  handleExplicitLanguageChange = (lng) => {
    this.explicitLanguageRevision = (this.explicitLanguageRevision || 0) + 1;
    this.languageSavePending = false;
    this.explicitDisplayLanguage = lng;
    this.handleI18nChange(lng, true);
  }
  handleI18nChange = (lng, explicit = false) => {
    if (!explicit && this.explicitDisplayLanguage && lng !== this.explicitDisplayLanguage) return;
    // A delayed startup read must not replace an unsaved form selection.
    if (!explicit && this.state.data && this.state.oriData
      && this.state.data.language !== this.state.oriData.language) return;
    this.observedDisplayLanguage = lng;
    if (this.state.data && (explicit || this.state.data.language !== lng)) {
      this.setState(prevState => ({
        data: {
          ...prevState.data,
          language: lng,
          currentLanguageCode: lng,
        },
        oriData: prevState.oriData ? {
          ...prevState.oriData,
          language: lng,
          currentLanguageCode: lng,
        } : prevState.oriData,
      }), () => {
        const { data, oriData } = this.state;
        if (profileHasChanges(data, oriData)) this.handleCheckChanges();
        else this.handleResetCheck();
      });
    }
  }
  handleOnCheck = (hasChanges = false) => {
    if (this.props.onCheck) {
      this.props.onCheck(hasChanges);
    }
  }
  handleCheckChanges() {
    if (!this.state.hasChanges) {
      this.setState({ hasChanges: true }, () => {
        this.handleOnCheck(true);
      });
    }
  }
  handleResetCheck() {
    if (this.state.hasChanges) {
      this.setState({ hasChanges: false }, () => {
        this.handleOnCheck(false);
      });
    }
  }
  handleOnChange = (event, value) => {
    const { data } = this.state;
    data[value.name] = value.value;
    if (value.name === 'language') {
      data.currentLanguageCode = value.value;
      data.currentLanguageSetAt = new Date().toISOString();
    }
    this.setState({ data }, () => {
      this.handleCheckChanges();
    });
  }
  handleCheckBoxChange = (event, value) => {
    const { data } = this.state;
    data[value.name] = !data[value.name];
    this.setState({ data }, () => {
      this.handleCheckChanges();
    });
  }
  handleEditProfile = () => {
    this.setState({ edit: true });
  }
  handleEditPassword = () => {
    this.setState({ editPassword: true });
  }
  handleChangeLanguage(currentLanguage) {
    const { i18n: displayI18n } = this.props;
    if (currentLanguage !== '') {
      this.explicitDisplayLanguage = currentLanguage;
      changeDisplayLanguage(displayI18n, currentLanguage);
    }
  }
  handleUpdateProfile = (event, silent = false) => {
    if (this.profileSaveInFlight) {
      return Promise.resolve();
    }

    const { data } = this.state;
    return AppForm.validate(this.validation, data)
      .then((validations) => {
        this.setState({
          errMessages: validations.filter(v => !v.isValid),
        });
        return this.putProfile(validations, this.state.data, silent)
        .catch((err) => {
          Bugsnag.notifyException(err);
          if (silent) throw err;
        });
      });
  }
  handleOnChangePassword = (event, value) => {
    const { passwords } = this.state;
    passwords[value.name] = value.value;

    this.setState({ passwords }, () => {
      this.handleCheckChanges();
    });
  }
  handleChangePassword = () => {
    const { passwords } = this.state;
    return AppForm.validate(this.passwordValidation, passwords)
      .then((passwordValidation) => {
        this.setState({
          errMessages: passwordValidation.filter(v => !v.isValid),
        });
        return this.changePassword(passwordValidation, passwords);
      });
  }
  handleResetChanges = () => {
    if (this.profileSaveInFlight) return;
    this.languageSavePending = false;
    this.explicitLanguageRevision = (this.explicitLanguageRevision || 0) + 1;
    const { oriData } = this.state;
    this.setState({
      data: clone(oriData),
      edit: false,
    }, () => {
      this.handleResetCheck();
    });
  }
  handleResetPasswordChanges = () => {
    this.setState({
      editPassword: false,
      passwordSaveWarning: null,
      passwords: {
        current: '',
        new: '',
        confirm: '',
      },
    }, () => {
      this.handleResetCheck();
    });
  }
  async fetchProfile() {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    let res;
    try {
      res = await store.dispatch(fetchM(`${config.get('apiURL')}/profile/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const token = res.headers.get('X-Csrf-Token');
      const data = await res.json();
      Object.assign(data, this.pendingAcceptedFields || {});
      this.pendingAcceptedFields = null;
      data.currentPassword = '';
      data.updatePassword = '';
      data.comfirmPassword = '';
      // A language event can arrive while the profile request is still loading.
      if (this.observedDisplayLanguage) {
        data.language = this.observedDisplayLanguage;
        data.currentLanguageCode = this.observedDisplayLanguage;
      }
      // handle token.. etc
      await this.setComponentState({
        token,
        data,
        oriData: clone(data),
      }, () => {
        this.handleResetCheck();
      });
    }
    await this.setLoading(false);
  }
  putProfile(validations, staff, silent = false) {
    if (this.profileSaveInFlight) return Promise.resolve();
    if (pendingPasswordChange && !silent) {
      this.context.store.dispatch(flashErrorMessage({ message: this.props.t('Wait for the pending profile or password request to finish before saving again.', { ns: TRANSLATION_FILES.translation }) }));
      return Promise.resolve();
    }
    this.profileSaveInFlight = true;
    this.setState({ profileSaving: true, profileSaveWarning: null, languageSaveWarning: null });
    this.profileSavePromise = this.persistProfile(validations, staff, silent).finally(() => {
      this.profileSaveInFlight = false;
      this.setState({ profileSaving: false });
    });
    return this.profileSavePromise;
  }
  async boundedProfileWrite(options, audit = false) {
    const unknown = () => Object.assign(new Error(this.props.t('Profile save is still unconfirmed. Your edits are preserved. Wait for the connection to recover before retrying.', { ns: TRANSLATION_FILES.translation })), { code: 'PROFILE_SAVE_UNCONFIRMED' });
    if (pendingProfileWrite) throw unknown();
    let timer;
    let expired = false;
    const operation = Promise.resolve().then(() => this.context.store.dispatch(fetchM(`${config.get('apiURL')}/profile/`, options)))
      .then(response => response.json(), async (error) => {
        if (error.res && error.res.status >= 400 && error.res.status < 500) {
          const message = await error.res.text();
          throw new Error(message || error.message);
        }
        throw error;
      });
    pendingProfileWrite = operation;
    pendingProfileIsAudit = audit;
    const userID = (this.props.auth || {}).id;
    const settled = (error) => {
      if (pendingProfileWrite === operation) pendingProfileWrite = null;
      profileWriteUnconfirmed = false;
      const fields = expired && !error ? JSON.parse(options.body) : null;
      if (fields) delete fields.updatePassword;
      notifyProfileStatus(fields, userID);
      if (fields && this.profileMounted !== false
        && !profileStatusListeners.has(this.updatePendingStatus)) {
        this.acceptProfileFields(fields);
      }
      if (expired) {
        if (!error) refreshUserCache().catch(err => Bugsnag.notifyException(err));
        // Reconcile the accepted baseline only; never replace entered form values.
        if (!audit && this.profileMounted !== false) {
          this.setState({ profileSaveWarning: error ? error.message : null });
        }
      }
    };
    operation.then(() => settled(null), settled);
    try {
      return await Promise.race([
        operation,
        new Promise((resolve, reject) => {
          timer = setTimeout(() => {
            expired = true;
            profileWriteUnconfirmed = true;
            notifyProfileStatus();
            reject(unknown());
          }, 5000);
        }),
      ]);
    } finally { clearTimeout(timer); }
  }
  acceptProfileFields(fields) {
    if (!this.state.oriData || !this.state.data) {
      this.pendingAcceptedFields = { ...this.pendingAcceptedFields, ...fields };
      return;
    }
    this.setState((previous) => {
      const oriData = { ...previous.oriData, ...fields };
      const hasChanges = profileHasChanges(previous.data, oriData);
      return { oriData, hasChanges, edit: previous.edit || hasChanges };
    }, () => this.handleOnCheck(this.state.hasChanges));
  }
  async persistProfile(validations, staff, silent = false) {
    const { store } = this.context;
    const { t } = this.props;
    const errors = validations.filter(v => !v.isValid);
    if (errors.length > 0) {
      this.setState({ edit: true });
      store.dispatch(flashErrorMessage({ header: t('Please fix form errors', { ns: TRANSLATION_FILES.translation }), list: errors.map(l => l.message) }));
      return;
    }
    const languageChanged = this.languageSavePending
      || staff.language !== this.state.oriData.language;
    let res;
    let profileFields;
    let refreshUser = false;
    let languageFailed = false;
    try {
      if (languageChanged) {
        this.languageSavePending = true;
        this.handleChangeLanguage(staff.language);
        const savedLanguage = staff.language;
        const selectionRevision = this.explicitLanguageRevision || 0;
        try {
          await saveLanguagePreference(savedLanguage);
          // Refresh once after the profile attempt, even if that unrelated PUT fails.
          refreshUser = true;
          this.languageSavePending = false;
          // Advance only the saved baseline, preserving edits made during the request.
          if (selectionRevision === (this.explicitLanguageRevision || 0)) {
            this.setState({
              oriData: {
                ...this.state.oriData,
                language: savedLanguage,
                currentLanguageCode: savedLanguage,
              },
            });
          }
        } catch (error) {
          languageFailed = true;
          if (error.code === 'LANGUAGE_SAVE_UNCONFIRMED') this.setState({ languageSaveWarning: t('Language save is still unconfirmed. This tab keeps your selection. Wait for the connection to recover before retrying.', { ns: TRANSLATION_FILES.translation }) });
          if (!error.blocked) Bugsnag.notifyException(error);
        } finally { this.languageSavePending = false; }
      }
      // The form may have changed while the preference write was pending.
      profileFields = Object.keys(this.state.data).reduce((changed, key) => (
        !isEqual(this.state.data[key], this.state.oriData[key])
          ? { ...changed, [key]: this.state.data[key] } : changed
      ), {});
      delete profileFields.updatePassword;
      delete profileFields.currentPassword;
      delete profileFields.confirmPassword;
      delete profileFields.comfirmPassword;
      delete profileFields.language;
      delete profileFields.currentLanguageCode;
      delete profileFields.currentLanguageSetAt;
      res = await this.boundedProfileWrite({
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.state.token,
        },
        body: JSON.stringify(profileFields),
      });
      refreshUser = true;
    } catch (err) {
      Bugsnag.notifyException(err);
      if (err.code === 'PROFILE_SAVE_UNCONFIRMED') this.setState({ profileSaveWarning: err.message });
      if (!silent) store.dispatch(flashErrorMessage(err));
      if (silent) throw err;
      return;
    } finally {
      if (refreshUser) {
        try {
          await refreshUserCache();
        } catch (error) { Bugsnag.notifyException(error); }
      }
    }
    const data = res;
    this.setState((previous) => {
      const oriData = { ...previous.oriData, ...profileFields };
      const hasChanges = profileHasChanges(previous.data, oriData);
      return { oriData, hasChanges, edit: hasChanges };
    }, () => {
      this.handleOnCheck(this.state.hasChanges);
      // Unrelated profile edits must not change this presentation tab's language.
      if (!silent && languageFailed) store.dispatch(flashErrorMessage({ message: t('Profile saved; language could not be saved. Please retry the language selection.', { ns: TRANSLATION_FILES.translation }) }));
      if (!silent && !languageFailed) store.dispatch(flashSuccessMessage({ header: t('Update Success', { ns: TRANSLATION_FILES.translation }), message: t('{{name}} successfully updated', { name: data.name, ns: TRANSLATION_FILES.translation }) }));
    });
  }
  changePassword = (validations, passwords) => {
    const { store } = this.context;
    const { t } = this.props;
    if (this.profileSaveInFlight || pendingProfileWrite || pendingPasswordChange) {
      store.dispatch(flashErrorMessage({ message: t('Wait for the pending profile or password request to finish before saving again.', { ns: TRANSLATION_FILES.translation }) }));
      return Promise.resolve();
    }
    const errors = validations.filter(v => !v.isValid);
    if (errors.length > 0) {
      this.setState({ editPassword: true });
      store.dispatch(flashErrorMessage({ header: t('Please fix form errors', { ns: TRANSLATION_FILES.translation }), list: errors.map(l => l.message) }));
      return Promise.resolve();
    }
    this.setState({ passwordSaving: true, passwordSaveWarning: null, auditSaveWarning: null });
    let timer;
    let expired = false;
    const operation = Promise.resolve().then(() => Auth.currentUserPoolUser())
      .then(user => Auth.changePassword(user, passwords.current, passwords.new))
      .then(async () => {
        if (this.profileMounted !== false) {
          this.setState({ editPassword: false, passwordSaveWarning: null, passwords: { current: '', new: '', confirm: '' } });
        }
        store.dispatch(flashSuccessMessage({ header: t('Update Success', { ns: TRANSLATION_FILES.translation }), message: t('Your password successfully updated', { ns: TRANSLATION_FILES.translation }) }));
        // Audit only: Cognito has already confirmed the password change.
        try {
          await this.boundedProfileWrite({
            credentials: 'include',
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-Csrf-Token': this.state.token },
            body: JSON.stringify({ updatePassword: 'changed' }),
          }, true);
        } catch (error) {
          Bugsnag.notifyException(error);
          const message = t('Password changed; the activity record could not be confirmed. Do not repeat the password change.', { ns: TRANSLATION_FILES.translation });
          if (this.profileMounted !== false) this.setState({ auditSaveWarning: message });
          store.dispatch(flashErrorMessage({ message }));
        }
      });
    pendingPasswordChange = operation;
    const settled = (error) => {
      if (expired && error) {
        Bugsnag.notifyException(error);
        store.dispatch(flashErrorMessage(error));
      }
      if (pendingPasswordChange === operation) pendingPasswordChange = null;
      if (this.profileMounted !== false) this.setState({ passwordSaveWarning: null });
    };
    operation.then(() => settled(null), settled);
    return Promise.race([operation, new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        expired = true;
        reject(Object.assign(new Error(t('Password change is still unconfirmed. Wait for the pending request to finish before retrying.', { ns: TRANSLATION_FILES.translation })), { code: 'PASSWORD_SAVE_UNCONFIRMED' }));
      }, 10000);
    })]).catch((err) => {
      Bugsnag.notifyException(err);
      if (this.profileMounted !== false && err.code === 'PASSWORD_SAVE_UNCONFIRMED') this.setState({ passwordSaveWarning: err.message });
      store.dispatch(flashErrorMessage(err));
    }).finally(() => {
      clearTimeout(timer);
      if (this.profileMounted !== false) this.setState({ passwordSaving: false });
    });
  }
  equalPassword = (value, opts) => {
    assertString(value);
    const validity = createValidity(opts.key);
    const { passwords } = this.state;
    const { t } = this.props;
    if (value !== '' && value !== passwords.new) {
      validity.isValid = false;
      validity.message = t('Confirm password does not match new password', { ns: TRANSLATION_FILES.translation });
    }
    return validity;
  }
  render() {
    const { auth, t } = this.props;
    const { data, passwords, editPassword } = this.state;
    const saving = this.state.loading || this.state.profileSaving || this.state.passwordSaving;
    const profileLanguageOptions = languageOptions(
      LANGUAGE_CODES, auth && auth.company && auth.company.activeLanguageCodes,
      data && data.language,
    ).map(l => ({ ...l, text: t(l.text, { ns: TRANSLATION_FILES.constants }) }));

    return (
      <Container>
        <NavBar
          header={t('Overview')}
        />
        <Grid>
          <Grid.Row>
            <Grid.Column>
              {this.state.auditSaveWarning && <p role="alert">{this.state.auditSaveWarning}</p>}
              {this.state.passwordSaveWarning && <p role="alert">{this.state.passwordSaveWarning}</p>}
              {this.state.languageSaveWarning && <p role="alert">{this.state.languageSaveWarning}</p>}
              {this.state.profileSaveWarning && <p role="alert">{this.state.profileSaveWarning}</p>}
              <Dimmer active={saving} inverted>
                <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
              </Dimmer>
              <Divider horizontal>{t('Profile Information')}</Divider>
              {data ?
                <EditableBox
                  editable
                  isEditing={this.state.edit}
                  loading={saving}
                  onEdit={this.handleEditProfile}
                  onClick={this.handleUpdateProfile}
                  onCancel={this.handleResetChanges}
                  content={({ isEditing, onConfirm }) => (
                    <Grid.Row>
                      <Grid.Column mobile={16} tablet={16} computer={4}>
                        {
                          this.state.oriData ?
                            <Grid>
                              <Grid.Row stretched>
                                <Grid.Column width={16}>
                                  <Header as="h2" textAlign="center">
                                    {this.state.oriData.preferredName}
                                    <Header.Subheader>
                                      <i><Timestamp format="MMMM Do YYYY, h:mm a" timestamp={this.state.oriData.createdAt} /></i>
                                    </Header.Subheader>
                                  </Header>
                                </Grid.Column>
                                <Grid.Column width={16}>
                                  <Divider hidden className="tablet-hide" />
                                </Grid.Column>
                              </Grid.Row>
                            </Grid> :
                          null
                        }
                        <Divider hidden className="large-hide small-hide" />
                      </Grid.Column>
                      <Grid.Column mobile={16} tablet={8} computer={6}>
                        <AppForm
                          onSubmit={onConfirm}
                          error={this.state.errMessages.length > 0}
                          ref={(ref) => { this.form = ref; }}
                        >
                          <Grid verticalAlign="middle">
                            <Grid.Column textAlign="left" width={8}>
                              <b>{t('Name')}</b>
                            </Grid.Column>
                            <Grid.Column textAlign="right" width={8}>
                              {
                                isEditing ?
                                  <AppForm.Input
                                    fluid
                                    name="name"
                                    onChange={this.handleOnChange}
                                    placeholder={t('Name')}
                                    value={data.name}
                                    ref={this.validation.name}
                                    validateLabel={t('Name')}
                                    validation={[required]}
                                  /> :
                                  data.name
                              }
                            </Grid.Column>
                          </Grid>
                          <Divider style={{ margin: '0.5em 0' }} />
                          <Grid verticalAlign="middle">
                            <Grid.Column textAlign="left" width={8}>
                              <b>{t('Preferred name')}</b>
                            </Grid.Column>
                            <Grid.Column textAlign="right" width={8}>
                              {
                                isEditing ?
                                  <AppForm.Input
                                    fluid
                                    name="preferredName"
                                    onChange={this.handleOnChange}
                                    placeholder={t('Preferred name')}
                                    value={data.preferredName}
                                    ref={this.validation.preferredName}
                                    validation={[required]}
                                    validateLabel={t('Preferred name')}
                                  /> :
                                  data.preferredName
                              }
                            </Grid.Column>
                          </Grid>
                          <Divider style={{ margin: '0.5em 0' }} />
                          <Grid verticalAlign="middle">
                            <Grid.Column textAlign="left" width={8}>
                              <b>{t('Email')}</b>
                            </Grid.Column>
                            <Grid.Column textAlign="right" width={8}>
                              <a href={`mailto:${data.email}`}>
                                {data.email}
                              </a>
                            </Grid.Column>
                          </Grid>
                          <Divider style={{ margin: '0.5em 0' }} />
                        </AppForm>
                      </Grid.Column>
                      <Grid.Column mobile={16} tablet={8} computer={6}>
                        <AppForm
                          onSubmit={onConfirm}
                          error={this.state.errMessages.length > 0}
                          ref={(ref) => { this.form = ref; }}
                        >
                          <Grid verticalAlign="middle">
                            {
                              isEditing ?
                                <Grid.Column textAlign="left" width={8}>
                                  <AppForm.Validate
                                    validation={[required]}
                                    ref={this.validation.mobilePrefix}
                                    onChange={this.handleOnChange}
                                  >
                                    <Form.Select
                                      label={t('Mobile prefix')}
                                      fluid
                                      search
                                      name="mobilePrefix"
                                      options={INTL_CALL_CODES}
                                      selectOnBlur={false}
                                      value={data.mobilePrefix}
                                      noResultsMessage={t('No results found', { ns: TRANSLATION_FILES.translation })}
                                    />
                                  </AppForm.Validate>
                                </Grid.Column> :
                                <Grid.Column textAlign="left" width={isEditing ? 4 : 8}>
                                  <b>{t('Mobile')}</b>
                                </Grid.Column>
                            }
                            <Grid.Column textAlign={isEditing ? 'left' : 'right'} width={8}>
                              {
                                isEditing ?
                                  <PhoneInput
                                    fluid
                                    label={t('Mobile')}
                                    name="mobile"
                                    onChange={this.handleOnChange}
                                    type="tel"
                                    placeholder="mobile"
                                    prefix={data.mobilePrefix}
                                    value={data.mobile}
                                    validation={[required]}
                                    validateLabel={t('Mobile')}
                                    ref={this.validation.mobile}
                                  /> :
                                  <Phone
                                    mobilePrefix={data.mobilePrefix}
                                    mobile={data.mobile}
                                  />
                              }
                            </Grid.Column>
                          </Grid>
                          <Divider style={{ margin: '0.5em 0' }} />
                          <Grid verticalAlign="middle">
                            {
                              isEditing ?
                                <Grid.Column textAlign="left" width={8}>
                                  <Form.Select
                                    label={t('Phone prefix')}
                                    fluid
                                    search
                                    name="officeTelPrefix"
                                    onChange={this.handleOnChange}
                                    options={INTL_CALL_CODES}
                                    selectOnBlur={false}
                                    value={data.officeTelPrefix}
                                    noResultsMessage={t('No results found', { ns: TRANSLATION_FILES.translation })}
                                  />
                                </Grid.Column> :
                                <Grid.Column textAlign="left" width={isEditing ? 4 : 8}>
                                  <b>{t('Phone')}</b>
                                </Grid.Column>
                            }
                            <Grid.Column textAlign={isEditing ? 'left' : 'right'} width={8}>
                              {
                                isEditing ?
                                  <PhoneInput
                                    fluid
                                    label={t('Phone')}
                                    name="officeTel"
                                    onChange={this.handleOnChange}
                                    type="tel"
                                    placeholder={t('Phone Number')}
                                    prefix={data.officeTelPrefix}
                                    value={data.officeTel}
                                  /> :
                                  <Phone
                                    mobilePrefix={data.officeTelPrefix}
                                    mobile={data.officeTel}
                                  />
                              }
                            </Grid.Column>
                          </Grid>
                          <Divider style={{ margin: '0.5em 0' }} />
                          <Grid verticalAlign="middle">
                            <Grid.Column textAlign="left" width={8}>
                              {
                                isEditing ?
                                  <Form.Select
                                    label={t('Default language')}
                                    fluid
                                    search
                                    name="language"
                                    onChange={this.handleOnChange}
                                    options={profileLanguageOptions}
                                    selectOnBlur={false}
                                    value={data.language}
                                    noResultsMessage={t('No results found', { ns: TRANSLATION_FILES.translation })}
                                  /> :
                                  <b>{t('Default language')}</b>
                              }
                            </Grid.Column>
                            {
                              !isEditing &&
                              <Grid.Column textAlign="right" width={8}>
                                {t(valueToText(LANGUAGE_CODES, data.language), {
                                  ns: TRANSLATION_FILES.constants,
                                })}
                              </Grid.Column>
                            }
                          </Grid>
                          {
                            !auth.isADFS && (!editPassword ? (
                              <Grid verticalAlign="middle">
                                <Grid.Column width="16" textAlign="right">
                                  <Button
                                    type="button"
                                    onClick={this.handleEditPassword}
                                    disabled={this.state.loading || isEditing}
                                  >{t('Change password')}</Button>
                                </Grid.Column>
                              </Grid>
                            ) : null)
                          }
                        </AppForm>
                      </Grid.Column>
                    </Grid.Row>
                  )}
                />
                  : <Grid>
                    {t('No staff found')}
                  </Grid>
              }
              {
                data && editPassword ? (
                  <EditableBox
                    editable
                    isEditing
                    loading={saving}
                    onClick={this.handleChangePassword}
                    onCancel={this.handleResetPasswordChanges}
                    content={({ onConfirm }) => (
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <AppForm
                            onSubmit={onConfirm}
                            error={this.state.errMessages.length > 0}
                            ref={(ref) => { this.form = ref; }}
                          >
                            <Grid verticalAlign="middle">
                              <Grid.Column textAlign="left" width={8}>
                                <b>{t('Password update')}:</b>
                              </Grid.Column>
                              <Grid.Column width={8}>
                                <AppForm.Input
                                  label={t('Current')}
                                  fluid
                                  name="current"
                                  type="password"
                                  value={passwords.current}
                                  validateLabel={t('Current password')}
                                  validation={[required]}
                                  ref={this.passwordValidation.current}
                                  onChange={this.handleOnChangePassword}
                                />
                                <AppForm.Input
                                  label={t('New', { ns: TRANSLATION_FILES.constants })}
                                  fluid
                                  name="new"
                                  type="password"
                                  validateLabel={t('Update password')}
                                  validation={[required]}
                                  value={passwords.new}
                                  ref={this.passwordValidation.new}
                                  onChange={this.handleOnChangePassword}
                                />
                                <AppForm.Input
                                  label={t('Re-type new')}
                                  fluid
                                  name="confirm"
                                  type="password"
                                  value={passwords.confirm}
                                  validateLabel={t('Confirm password')}
                                  validation={[required, this.equalPassword]}
                                  ref={this.passwordValidation.confirm}
                                  onChange={this.handleOnChangePassword}
                                />
                              </Grid.Column>
                            </Grid>
                          </AppForm>
                        </Grid.Column>
                      </Grid.Row>
                    )}
                  />
                ) : null
              }
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(connect(Overview.mapStateToProps)(Overview));



// WEBPACK FOOTER //
// ./src/containers/Profile/Overview.jsx